var Model = require('../Model');

var Report = Object.create(Model);
var CUSTOM_EXPORT_HELP_TEXT = 'Exports every dataset attached to the report as raw Excel worksheets. Use it when you need point-level tabular data for analysis outside the formatted plugin reports.';

Report.name = 'Report';
Report.url = 'report';
Report['@class'] = 'Report';
Report.fields = {
  parent: {
    default: null,
    hidden: true
  },
  name: {
  	type: 'text',
    validators: {
      presence: true, 
      length: {minimum: 3, maximum: 64, default: ''},
    }
  },
  description: {
    type: 'textarea',
    default: '',
    validators: {
      length: {maximum: 2000, default: ''}
    }
  },
  type: {
    type: 'select',
    default: 'Custom Export',
    options: [
      {name: 'Custom Export', value: 'Custom Export'}
    ]
  },
  locationRid: {
    default: null,
    hidden: true
  },
  eventConfig: {
    default: null,
    hidden: true
  },
  equipment: {
    default: null,
    hidden: true
  },
  exportConfig: {
    default: null,
    hidden: true
  },
};

Report.reportTypeMeta = {
  'Custom Export': {
    displayName: 'Custom Export',
    entrypoints: ['reportRecord'],
    ui: {modal: 'default', hasConfigPage: true, component: 'custom', helpText: CUSTOM_EXPORT_HELP_TEXT},
    event: {},
    download: {
      requiresEvent: false,
      eventSelection: false,
      dateSelection: { enabled: true, mode: 'window' },
      deliveryMode: 'queued',
      formats: ['xlsx']
    }
  }
};
Report.reportDefinitions = {
  'Custom Export': {
    id: 'Custom Export',
    templateName: 'Custom Export',
    entryName: null,
    entrypoints: ['reportRecord'],
    dataScope: 'reportRecord',
    ui: {modal: 'default', hasConfigPage: true, component: 'custom', helpText: CUSTOM_EXPORT_HELP_TEXT},
    download: {
      requiresEvent: false,
      eventSelection: false,
      dateSelection: { enabled: true, mode: 'window' },
      deliveryMode: 'queued',
      formats: ['xlsx']
    },
    schedule: {
      defaultParams: {
        useNow: true,
        span: 7,
        resample: 'days',
        bucket: '3_years',
        intervalMinutes: 15,
        statistic: 'mean'
      }
    },
    variants: [
      {
        dataScope: 'reportRecord',
        targetConfig: { targetKind: 'reportRecord' }
      }
    ]
  }
};

function normalizeDownloadFormats(formats, fallbackFormats) {
  var allowed = Array.isArray(formats) ? formats.slice() : [];
  var defaults = Array.isArray(fallbackFormats) && fallbackFormats.length
    ? fallbackFormats.slice()
    : ['pdf', 'xlsx'];
  var normalized = allowed
    .map(function (value) { return String(value || '').toLowerCase(); })
    .filter(function (value) { return value === 'pdf' || value === 'xlsx' || value === 'json'; });

  if (!normalized.length) {
    normalized = defaults
      .map(function (value) { return String(value || '').toLowerCase(); })
      .filter(function (value) { return value === 'pdf' || value === 'xlsx' || value === 'json'; });
  }

  return Array.from(new Set(normalized));
}

function normalizeDateSelectionConfig(value) {
  if (value === true) {
    return { enabled: true, mode: 'window' };
  }
  if (typeof value === 'string') {
    return { enabled: true, mode: value === 'range' ? 'range' : 'window' };
  }
  if (value && typeof value === 'object') {
    var enabled = Object.prototype.hasOwnProperty.call(value, 'enabled')
      ? value.enabled !== false
      : true;
    return {
      enabled: enabled,
      mode: value.mode === 'range' ? 'range' : 'window'
    };
  }
  return { enabled: false, mode: null };
}

function normalizeDownloadMeta(download, typeName) {
  var fallbackFormats = typeName === 'Custom Export' ? ['xlsx'] : ['pdf', 'xlsx'];
  var meta = download && typeof download === 'object' ? Object.assign({}, download) : {};
  var selection = meta.selection && typeof meta.selection === 'object' ? meta.selection : {};
  var rawDateSelection = Object.prototype.hasOwnProperty.call(meta, 'dateSelection')
    ? meta.dateSelection
    : selection.date;
  var rawEventSelection = Object.prototype.hasOwnProperty.call(meta, 'eventSelection')
    ? meta.eventSelection
    : selection.event;

  meta.formats = normalizeDownloadFormats(meta.formats, fallbackFormats);
  meta.requiresEvent = Boolean(meta.requiresEvent);
  meta.eventSelection = Object.prototype.hasOwnProperty.call(meta, 'eventSelection') || Object.prototype.hasOwnProperty.call(selection, 'event')
    ? Boolean(rawEventSelection)
    : meta.requiresEvent;
  meta.dateSelection = normalizeDateSelectionConfig(rawDateSelection);
  return meta;
}

Report.setReportTypes = function (types, metadata) {
  if (Array.isArray(types) && types.length > 0 && Report.fields && Report.fields.type) {
    Report.fields.type.options = types;
  }
  Report.reportTypeMeta = metadata || Report.reportTypeMeta || {};
};

Report.applyDefinitions = function (definitions) {
  var nextDefinitions = {
    'Custom Export': Object.assign({}, Report.reportDefinitions['Custom Export'])
  };

  (Array.isArray(definitions) ? definitions : []).forEach(function (definition) {
    if (!definition || !definition.id) return;
    nextDefinitions[definition.id] = Object.assign({}, definition);
    (definition.legacyAliases || []).forEach(function (alias) {
      if (!alias || nextDefinitions[alias]) return;
      nextDefinitions[alias] = nextDefinitions[definition.id];
    });
  });

  Report.reportDefinitions = nextDefinitions;
};

Report.applyTypeManifest = function (manifestTypes) {
  const options = [{name: 'Custom Export', value: 'Custom Export'}];
  const meta = {
    'Custom Export': {
      displayName: 'Custom Export',
      entrypoints: ['reportRecord'],
      ui: {modal: 'default', hasConfigPage: true, component: 'custom', helpText: CUSTOM_EXPORT_HELP_TEXT},
      download: normalizeDownloadMeta({
        requiresEvent: false,
        eventSelection: false,
        dateSelection: { enabled: true, mode: 'window' },
        deliveryMode: 'queued',
        formats: ['xlsx']
      }, 'Custom Export')
    }
  };

  (Array.isArray(manifestTypes) ? manifestTypes : []).forEach(type => {
    if (!type || !type.name) return;
    var ui = type.ui || {};
    var metaKey = type.value || type.name;
    if (metaKey === 'Custom Export') return;
    var metaRecord = {
      displayName: type.name,
      value: metaKey,
      legacyAliases: type.legacyAliases || [],
      entrypoints: type.entrypoints || [],
      ui: ui,
      event: type.event || {},
      download: normalizeDownloadMeta(type.download, metaKey),
      classDef: type.classDef || null
    };
    if (ui.hiddenFromCreate !== true) {
      options.push({name: type.name, value: metaKey});
    }
    meta[metaKey] = metaRecord;
    (type.legacyAliases || []).forEach(function (alias) {
      if (!alias || meta[alias]) return;
      meta[alias] = metaRecord;
    });
  });

  Report.setReportTypes(options, meta);
};

Report.applyRegistryPayload = function (payload) {
  var response = payload && typeof payload === 'object' ? payload : {};
  Report.applyDefinitions(response.definitions || []);
  Report.applyTypeManifest(response.types || []);
};

Report.getTypeMeta = function (typeName) {
  return Report.reportTypeMeta[typeName]
    || Report.reportDefinitions[typeName]
    || {};
};

Report.getDownloadMeta = function (typeName) {
  var meta = Report.getTypeMeta(typeName);
  return normalizeDownloadMeta(meta && meta.download ? meta.download : {}, typeName);
};

Report.getAllowedDownloadFormats = function (typeName) {
  return Report.getDownloadMeta(typeName).formats || [];
};

Report.supportsDownloadFormat = function (typeName, format) {
  return Report.getAllowedDownloadFormats(typeName).indexOf(String(format || '').toLowerCase()) >= 0;
};

Report.getDefinition = function (typeName) {
  return Report.reportDefinitions[typeName] || {};
};

Report.getScheduleDefaults = function (typeName) {
  var definition = Report.getDefinition(typeName);
  return definition && definition.schedule ? definition.schedule : {};
};

Report.getDefinitionVariant = function (typeName, matcher) {
  var definition = Report.getDefinition(typeName);
  var variants = definition && Array.isArray(definition.variants) ? definition.variants : [];
  matcher = matcher || {};
  return variants.find(function (variant) {
    if (!variant) return false;
    if (matcher.dataScope && variant.dataScope !== matcher.dataScope) return false;
    if (matcher.targetKind) {
      var targetKind = variant.targetConfig && variant.targetConfig.targetKind;
      if (targetKind !== matcher.targetKind) return false;
    }
    return true;
  }) || null;
};

module.exports = Report;
